<script setup lang="ts">
import EntityCardShell from '@/components/entity/EntityCardShell.vue';
import EntityActionButton from '@/components/entity/EntityActionButton.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ContextMenuContent, ContextMenuItem, ContextMenuSeparator } from '@/components/ui/context-menu';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import type { Client } from '~/types/models';
import {
    Copy,
    Eye,
    Mail,
    MoreHorizontal,
    Pencil,
    UserRound,
    Workflow,
} from 'lucide-vue-next';

interface Props {
    client: Client;
    href: string;
    title: string;
    subtitle: string;
    imageUrl?: string;
    initials: string;
    statusLabel: string;
    stageLabel?: string;
    sourceLabel?: string;
    updatedLabel: string;
    nextMoveLabel: string;
    canMutate?: boolean;
    portalLink?: string | null;
    surface?: 'default' | 'embedded' | 'list';
}

const props = withDefaults(defineProps<Props>(), {
    imageUrl: '',
    stageLabel: '',
    sourceLabel: '',
    canMutate: false,
    portalLink: null,
    surface: 'default',
});

const emit = defineEmits<{
    (e: 'edit', client: Client): void,
    (e: 'view-portal', client: Client): void,
    (e: 'copy-portal', client: Client): void,
    (e: 'send-digest', client: Client): void,
}>();
</script>

<template>
    <EntityCardShell
        :href="href"
        :title="title"
        :subtitle="subtitle"
        :image-url="imageUrl || ''"
        :image-alt="`${title} preview`"
        :fallback-text="initials"
        :stretch-media="true"
        :surface="surface"
        fallback-class="bg-primary text-primary-foreground"
    >
        <template #badges>
            <Badge variant="secondary">
                {{ statusLabel }}
            </Badge>
            <!-- <Badge v-if="stageLabel" variant="outline">
                {{ stageLabel }}
            </Badge> -->
            <!-- <Badge v-if="sourceLabel" variant="outline">
                {{ sourceLabel }}
            </Badge> -->
        </template>

        <template #mobile-actions>
            <Button size="sm" class="rounded-full px-4 shadow-sm" as-child>
                <NuxtLink :to="href">
                    <Eye class="mr-2 size-4" />
                    Open client
                </NuxtLink>
            </Button>
            <DropdownMenu>
                <DropdownMenuTrigger as-child>
                    <Button variant="ghost" size="icon" class="entity-row-action" aria-label="More client actions">
                        <MoreHorizontal class="size-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" class="w-56">
                    <DropdownMenuItem as-child>
                        <NuxtLink :to="href">
                            <Eye class="mr-2 size-4" />
                            View
                        </NuxtLink>
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="canMutate" @click="emit('edit', client)">
                        <Pencil class="mr-2 size-4" />
                        Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem v-if="portalLink" @click="emit('view-portal', client)">
                        <Eye class="mr-2 size-4" />
                        View portal as client
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="portalLink" @click="emit('copy-portal', client)">
                        <Copy class="mr-2 size-4" />
                        Copy portal link
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="portalLink" @click="emit('send-digest', client)">
                        <Mail class="mr-2 size-4" />
                        Send digest
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </template>

        <template #actions>
            <EntityActionButton label="Open client" :icon="Eye" :href="href" />
            <DropdownMenu>
                <DropdownMenuTrigger as-child>
                    <Button variant="ghost" size="icon" class="entity-row-action" aria-label="More client actions">
                        <MoreHorizontal class="size-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" class="w-56">
                    <DropdownMenuItem as-child>
                        <NuxtLink :to="href">
                            <Eye class="mr-2 size-4" />
                            View
                        </NuxtLink>
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="canMutate" @click="emit('edit', client)">
                        <Pencil class="mr-2 size-4" />
                        Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem v-if="portalLink" @click="emit('view-portal', client)">
                        <Eye class="mr-2 size-4" />
                        View portal as client
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="portalLink" @click="emit('copy-portal', client)">
                        <Copy class="mr-2 size-4" />
                        Copy portal link
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="portalLink" @click="emit('send-digest', client)">
                        <Mail class="mr-2 size-4" />
                        Send digest
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </template>

        <template #body>
            <div class="grid gap-3 pt-2 text-xs text-muted-foreground md:grid-cols-[minmax(0,2fr)_minmax(0,5fr)]">
                <!-- <div class="flex min-w-0 items-center gap-2">
                    <Workflow class="size-3.5 shrink-0 text-primary" />
                    <span class="truncate"><span class="font-medium text-foreground">Lifecycle:</span> {{ statusLabel }}</span>
                </div> -->
                <div class="flex min-w-0 items-center gap-2">
                    <Workflow class="size-3.5 shrink-0 text-primary" />
                    <span class="truncate"><span class="font-medium text-foreground">Next:</span> {{ nextMoveLabel }}</span>
                </div>
                <div class="flex min-w-0 items-center gap-2">
                    <UserRound class="size-3.5 shrink-0 text-primary" />
                    <span class="truncate"><span class="font-medium text-foreground">Primary:</span> {{ subtitle }}</span>
                </div>
                <!-- <div class="flex min-w-0 items-center gap-2">
                    <Mail class="size-3.5 shrink-0 text-primary" />
                    <span class="truncate"><span class="font-medium text-foreground">Source:</span> {{ sourceLabel || 'Client record' }}</span>
                </div> -->
                <!-- <div class="flex min-w-0 items-center gap-2">
                    <span class="font-medium text-foreground">Updated:</span>
                    <span class="truncate">{{ updatedLabel }}</span>
                </div> -->
            </div>
        </template>

        <template #context-menu>
            <ContextMenuContent class="w-60">
                <ContextMenuItem as-child>
                    <NuxtLink :to="href">
                        <Eye class="mr-2 size-4" />
                        View
                    </NuxtLink>
                </ContextMenuItem>
                <ContextMenuItem v-if="canMutate" @click="emit('edit', client)">
                    <Pencil class="mr-2 size-4" />
                    Edit
                </ContextMenuItem>
                <ContextMenuSeparator />
                <ContextMenuItem v-if="portalLink" @click="emit('view-portal', client)">
                    <Eye class="mr-2 size-4" />
                    View portal as client
                </ContextMenuItem>
                <ContextMenuItem v-if="portalLink" @click="emit('copy-portal', client)">
                    <Copy class="mr-2 size-4" />
                    Copy portal link
                </ContextMenuItem>
                <ContextMenuItem v-if="portalLink" @click="emit('send-digest', client)">
                    <Mail class="mr-2 size-4" />
                    Send digest
                </ContextMenuItem>
            </ContextMenuContent>
        </template>
    </EntityCardShell>
</template>
